import { useState, useRef, useMemo, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useGame } from "../../src/stores/useGame";

export const usePersonMovement = (
  rigidBodyRef: React.RefObject<any>,
  position: [number, number, number],
  route: [number, number, number][],
  npcId: string,
  moveSpeed: number = 1.5,
  stopDuration: number = 3,
  interactionDistance: number = 3
) => {
  const [currentWaypoint, setCurrentWaypoint] = useState(0);
  const [isMoving, setIsMoving] = useState(true);
  const [isStopped, setIsStopped] = useState(false);
  const [stopTimer, setStopTimer] = useState(0);
  const [showText, setShowText] = useState(false);
  const [isLookingAtPlayer, setIsLookingAtPlayer] = useState(false);
  
  const currentRotationRef = useRef(0);
  const targetRotationRef = useRef(0);
  const throttleCounter = useRef(0);

  const playerPosition = useGame((state) => state.playerPosition);
  const setNearNPC = useGame((state) => state.setNearNPC);
  const setNpcPosition = useGame((state) => state.setNpcPosition);

  // Waypoints
  const waypoints = useMemo(() => {
    const basePos = new THREE.Vector3(position[0], position[1], position[2]);
    const routePoints = route.map(point => new THREE.Vector3(point[0], point[1], point[2]));
    return [basePos.clone(), ...routePoints];
  }, [position, route]);

  // Initial rotation
  useEffect(() => {
    if (rigidBodyRef.current && waypoints.length > 0) {
      const currentPos = rigidBodyRef.current.translation();
      const targetWaypoint = waypoints[0];
      const direction = targetWaypoint.clone().sub(new THREE.Vector3(currentPos.x, currentPos.y, currentPos.z));
      const angle = Math.atan2(direction.x, direction.z);
      currentRotationRef.current = angle;
      targetRotationRef.current = angle;
    }
  }, [waypoints, rigidBodyRef]);

  // Near NPC effect - manage pointer lock and store state
  useEffect(() => {
    if (showText) {
      setNearNPC(true);
      if (document.pointerLockElement) {
        document.exitPointerLock?.();
      }
    } else {
      setNearNPC(false);
    }
  }, [showText, setNearNPC]);

  const getPersonPosition = () => {
    if (!rigidBodyRef.current) return null;
    const currentPos = rigidBodyRef.current.translation();
    return new THREE.Vector3(currentPos.x, currentPos.y, currentPos.z);
  };

  const lookAtPlayer = () => {
      if (playerPosition && rigidBodyRef.current) {
        const personPos = getPersonPosition();
        if (personPos) {
          const directionToPlayer = playerPosition.clone().sub(personPos);
          directionToPlayer.y = 0;
          if (directionToPlayer.length() > 0.1) {
            directionToPlayer.normalize();
            const targetAngle = Math.atan2(directionToPlayer.x, directionToPlayer.z);
            targetRotationRef.current = targetAngle;
          }
        }
      }
  };

  useFrame((state, delta) => {
    if (!rigidBodyRef.current) return;

    // Report position to global store (throttled to every 10 frames to save performance)
    throttleCounter.current += 1;
    if (throttleCounter.current >= 10) {
        const pos = rigidBodyRef.current.translation();
        setNpcPosition(npcId, { x: pos.x, y: pos.y, z: pos.z });
        throttleCounter.current = 0;
    }

    // Smooth rotation logic
    const rotationSpeed = 3;
    const angleDiff = targetRotationRef.current - currentRotationRef.current;
    let normalizedDiff = angleDiff;
    while (normalizedDiff > Math.PI) normalizedDiff -= 2 * Math.PI;
    while (normalizedDiff < -Math.PI) normalizedDiff += 2 * Math.PI;
    
    if (Math.abs(normalizedDiff) > 0.01) {
        const rotationStep = Math.sign(normalizedDiff) * rotationSpeed * delta;
        const actualStep = Math.abs(rotationStep) > Math.abs(normalizedDiff) ? normalizedDiff : rotationStep;
        currentRotationRef.current += actualStep;
        
        rigidBodyRef.current.setRotation(
            new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), currentRotationRef.current), 
            true
        );
    }

    const personPos = getPersonPosition();
    const targetWaypoint = waypoints[currentWaypoint];

    // Check player proximity
    if (playerPosition && personPos) {
      const distance = personPos.distanceTo(playerPosition);
      if (distance < interactionDistance) {
        if (!showText) {
          setShowText(true);
          setIsLookingAtPlayer(true);
        }
        rigidBodyRef.current.setLinvel(new THREE.Vector3(0, 0, 0), true);
        lookAtPlayer();
        return; // Pause movement logic
      } else {
        if (showText) {
          setShowText(false);
          setIsLookingAtPlayer(false);
        }
      }
    }

    // Movement logic
    if (isMoving && !isStopped && !isLookingAtPlayer && personPos) {
        const direction = targetWaypoint.clone().sub(personPos);
        const distance = direction.length();
        
        if (distance > 0.1) {
            direction.normalize();
            const velocity = direction.multiplyScalar(moveSpeed);
            rigidBodyRef.current.setLinvel(velocity, true);
            
            // Set rotation target
            const angle = Math.atan2(direction.x, direction.z);
            targetRotationRef.current = angle;
        } else {
            // Reached waypoint
            rigidBodyRef.current.setLinvel(new THREE.Vector3(0, 0, 0), true);
            setIsMoving(false);
            setIsStopped(true);
            setStopTimer(stopDuration);
        }
    }

    // Stop timer logic
    if (isStopped && !showText && !isLookingAtPlayer) {
        setStopTimer(prev => {
            const newTimer = prev - delta;
            if (newTimer <= 0) {
                setCurrentWaypoint(prev => (prev + 1) % waypoints.length);
                setIsMoving(true);
                setIsStopped(false);
                return 0;
            }
            return newTimer;
        });
    }
  });

  return { isStopped, showText, isLookingAtPlayer };
};
